import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo
} from 'react';
import PropTypes from 'prop-types';
import Collapse from '@mui/material/Collapse';
import {
  addGroupsToOptions,
  groupifyOptions,
  filterSelectedOptions,
  getFirstFocusableIndex,
  getLastFocusableIndex
} from '../helper';
import { baseSelectMenuClasses as classes } from './baseSelectMenuClasses';
import {
  BaseSelectMenuRoot,
  MenuContainer as DefaultMenuContainer,
  MenuList as DefaultMenuList,
  SearchContainer as DefaultSearchContainer
} from './BaseSelectMenu.style';

const CategoryOption = ({
  data,
  isCollapsed,
  component: Component,
  ...rest
}) => {
  return (
    <Component
      isGroup
      data={data}
      preSelectedValues={[]}
      isPreSelectedItem={false}
      data-qa-group-id={`group-${data.value}`}
      data-qa-state={isCollapsed ? 'collapsed' : 'expanded'}
      {...rest}
    />
  );
};

const BaseSelectMenu = forwardRef(
  (
    {
      menuIsOpen,
      options,
      values = [],
      isSearchable,
      SearchInputProps,
      MenuProps,
      onKeyDown = () => {},
      onClick,
      onFocusedItemChange,
      isAutoFocus = true,
      isLoading,
      lastSelected,
      preSelectedValues = [],
      components,
      maxValues = Infinity,
      isOptionDisabled = () => {},
      isMultiSelectionMode = false,
      isGroupsCollapsedByDefault = false,
      reverseOptionsOrder,
      slots,
      hasMenuHeader,
      showGroupCounter: incomingShowGroupCounter,
      ...rest
    },
    ref
  ) => {
    const filteredOptions = filterSelectedOptions(options, preSelectedValues);
    const [groups, itemsWithoutGroup] = groupifyOptions(filteredOptions);
    const optionsSelectedOrder = reverseOptionsOrder
      ? [...itemsWithoutGroup, ...groups]
      : [...groups, ...itemsWithoutGroup];
    const optionsToRender = [...preSelectedValues, ...optionsSelectedOrder];

    const isOptionForbidden = useCallback(
      item => {
        if (item) {
          const maxSelectionReach = values.length >= maxValues;
          const isSelected = values.some(
            selectedOption => selectedOption.value === item.value
          );
          return maxSelectionReach && !isSelected;
        }
      },
      [values, maxValues]
    );

    const isDisable = useCallback(
      item =>
        !!(
          item?.isDisabled ||
          isOptionDisabled(item) ||
          isOptionForbidden(item)
        ),
      [isOptionDisabled, isOptionForbidden]
    );

    const isAutoFocusMenu =
      isAutoFocus && (!isSearchable || onFocusedItemChange);

    const [collapsedGroups, setCollapsedGroups] = useState(() =>
      groups.reduce((acc, item) => {
        if (!item?.groupName) {
          return acc;
        }
        return { ...acc, [item.groupName]: isGroupsCollapsedByDefault };
      }, {})
    );

    const focusableOptions = useMemo(
      () => [
        ...preSelectedValues,
        ...addGroupsToOptions(filteredOptions).filter(option => {
          const isOptionVisible = !collapsedGroups[option.groupName];
          const isDisabledOption = isDisable(option);
          return !isDisabledOption && (!!option.isGroup || isOptionVisible);
        })
      ],
      [collapsedGroups, filteredOptions, isDisable, preSelectedValues]
    );

    const [focusedValue, setFocusedValue] = useState(() =>
      isAutoFocusMenu ? focusableOptions.find(o => !isDisable(o))?.value : null
    );
    const [focusedIndex, setFocusedIndex] = useState(-1);

    useEffect(() => {
      // sync focused index
      const initialIndex = isAutoFocus ? 0 : -1;
      const navigatedIndex = navigationRefs.current.findIndex(
        ({ item }) => item.value === focusedValue
      );
      const nextIndex = Math.max(navigatedIndex, initialIndex);
      setFocusedIndex(nextIndex);
    }, [focusedValue, isAutoFocus]);

    const navigationRefs = useRef([]);
    const menuListElRef = useRef();
    const menuElRef = useRef();
    const {
      Search,
      NoOptionsMessage,
      Option,
      Menu,
      LoadingMessage
    } = components;

    const currentMenuListElRef = menuListElRef.current;
    const currentMenuElRef = menuElRef.current;

    const handleNavigationOnArrowKeyPress = useCallback(
      key => {
        const navigationElements = navigationRefs.current;
        let newFocusedIndex = focusedIndex;
        const step = 1;
        const pageStep = 5;

        if (key === 'ArrowDown') {
          let nextPossibleStep = focusedIndex + step;

          while (nextPossibleStep < navigationElements.length) {
            const { isFocusable, ref } = navigationElements[nextPossibleStep];
            if (isFocusable && ref) {
              break;
            }
            nextPossibleStep += step;
          }

          const isOutOfBoundaries =
            nextPossibleStep >= navigationElements.length;

          if (isOutOfBoundaries) {
            newFocusedIndex = getFirstFocusableIndex(
              focusableOptions,
              navigationElements
            );
          } else {
            newFocusedIndex = nextPossibleStep;
          }
        }
        if (key === 'ArrowUp') {
          let nextPossibleStep = focusedIndex - step;

          while (nextPossibleStep >= 0) {
            const { isFocusable, ref } = navigationElements[nextPossibleStep];
            if (isFocusable && ref) {
              break;
            }
            nextPossibleStep -= step;
          }

          const isOutOfBoundaries = nextPossibleStep < 0;
          if (isOutOfBoundaries) {
            newFocusedIndex = getLastFocusableIndex(
              focusableOptions,
              navigationElements
            );
          } else {
            newFocusedIndex = nextPossibleStep;
          }
        }
        if (key === 'PageDown') {
          let nextPossibleStep = focusedIndex + pageStep;

          while (nextPossibleStep < navigationElements.length) {
            const { isFocusable, ref } = navigationElements[nextPossibleStep];
            if (isFocusable && ref) {
              break;
            }
            nextPossibleStep += pageStep;
          }

          const isOutOfBoundaries =
            nextPossibleStep >= navigationElements.length;

          if (isOutOfBoundaries) {
            newFocusedIndex = getLastFocusableIndex(
              focusableOptions,
              navigationElements
            );
          } else {
            newFocusedIndex = nextPossibleStep;
          }
        }
        if (key === 'PageUp') {
          let nextPossibleStep = focusedIndex - pageStep;

          while (nextPossibleStep >= 0) {
            const { isFocusable, ref } = navigationElements[nextPossibleStep];
            if (isFocusable && ref) {
              break;
            }
            nextPossibleStep -= pageStep;
          }

          const isOutOfBoundaries = nextPossibleStep < 0;

          if (isOutOfBoundaries) {
            newFocusedIndex = getFirstFocusableIndex(
              focusableOptions,
              navigationElements
            );
          } else {
            newFocusedIndex = nextPossibleStep;
          }
        }
        if (key === 'End') {
          newFocusedIndex = getLastFocusableIndex(
            focusableOptions,
            navigationElements
          );
        }
        if (key === 'Home') {
          newFocusedIndex = getFirstFocusableIndex(
            focusableOptions,
            navigationElements
          );
        }

        const menuRect = menuListElRef.current.getBoundingClientRect();
        const nextFocusedOptionEl = navigationElements[newFocusedIndex].ref;
        const nextFocusedElementHeight = nextFocusedOptionEl.offsetHeight;
        const hasGroups = focusableOptions.some(o => o.isGroup);

        let originalFocusedElementStylePosition = '';
        if (hasGroups) {
          // groups has a sticky position, which means we won't get the correct values for distance from top etc..
          // this is mostly relevant when navigating upwards (arrow up/ home keys) and we have overflowed items which we can't see.
          // so override the position to relative (and will restore it back after calculations)
          // keep in mind that reading/writing styles could be an expensive calculation, this is why we do it under certain conditions.
          originalFocusedElementStylePosition = nextFocusedOptionEl.style.getPropertyValue(
            'position'
          );
          nextFocusedOptionEl.style.setProperty(
            'position',
            'relative',
            'important'
          );
        }
        const distanceOfFocusedElementFromTop = nextFocusedOptionEl.offsetTop;
        const focusedRect = nextFocusedOptionEl.getBoundingClientRect();

        const isOutOfRangeOfBottomMenu =
          focusedRect.bottom + nextFocusedElementHeight > menuRect.bottom;
        const isOutOfRangeOfTopMenu =
          focusedRect.top - nextFocusedElementHeight < menuRect.top;
        if (isOutOfRangeOfBottomMenu) {
          const distanceFromTopWithElementHeight =
            distanceOfFocusedElementFromTop + nextFocusedElementHeight;
          const menuHeight = menuListElRef.current.offsetHeight;
          menuListElRef.current.scrollTop =
            distanceFromTopWithElementHeight -
            menuHeight +
            nextFocusedElementHeight;
        } else if (isOutOfRangeOfTopMenu) {
          const distanceFromTopWithElementHeight = distanceOfFocusedElementFromTop;

          menuListElRef.current.scrollTop =
            distanceFromTopWithElementHeight - nextFocusedElementHeight;
        }
        if (hasGroups) {
          // reset position back
          nextFocusedOptionEl.style.setProperty(
            'position',
            originalFocusedElementStylePosition
          );
        }
        const nextFocusedItem = navigationElements[newFocusedIndex].item;
        const nextFocusedValue = nextFocusedItem.value;
        setFocusedValue(nextFocusedValue);
        if (onFocusedItemChange) {
          const focusedItem = nextFocusedItem;
          onFocusedItemChange(focusedItem);
        }
      },
      [focusedIndex, focusableOptions, onFocusedItemChange]
    );

    const handleGroupClick = groupName => {
      setCollapsedGroups(prevState => ({
        ...prevState,
        [groupName]: !prevState[groupName]
      }));
    };

    const handleKeyDown = useCallback(
      e => {
        if (
          options.length !== 0 &&
          (e.key === 'ArrowDown' ||
            e.key === 'ArrowUp' ||
            e.key === 'End' ||
            e.key === 'Home' ||
            e.key === 'PageUp' ||
            e.key === 'PageDown')
        ) {
          e.preventDefault();
          e.stopPropagation();
          handleNavigationOnArrowKeyPress(e.key);
        }

        const focusedItem = focusableOptions.find(
          o => o.value === focusedValue
        );
        const shouldOpen = e.key === 'ArrowRight';
        const shouldClose = e.key === 'ArrowLeft';

        if (focusedItem?.isGroup && (shouldClose || shouldOpen)) {
          const groupName = focusedItem.value;
          setCollapsedGroups(prevState => ({
            ...prevState,
            [groupName]: shouldClose || !shouldOpen
          }));
        } else if (!isDisable(focusedItem)) {
          onKeyDown(e, focusedItem);
        }
      },
      [
        options,
        focusableOptions,
        focusedValue,
        handleNavigationOnArrowKeyPress,
        onKeyDown,
        isDisable
      ]
    );

    const handleCloseMenu = e => {
      if (e.key === 'Escape' && onKeyDown) {
        onKeyDown(e);
      }
    };

    useImperativeHandle(ref, () => ({
      handleKeyDown
    }));

    useEffect(() => {
      // skip in case there's a search input as we want to keep the focus on it
      if (isSearchable) {
        return;
      }

      if (menuListElRef.current && menuElRef.current && menuIsOpen) {
        if (!isAutoFocusMenu) {
          menuElRef.current.focus();
        } else {
          menuListElRef.current.focus();
        }
      }
    }, [
      menuIsOpen,
      isAutoFocusMenu,
      isSearchable,
      currentMenuListElRef,
      currentMenuElRef
    ]);

    const createRefSetter = ({ index, isFocusable, item }) => element => {
      navigationRefs.current[index] = { ref: element, isFocusable, item };
    };

    let navigationIterator = -1;

    // enable customization of internal components from parent through styled
    const MenuContainer = slots?.menuContainer || DefaultMenuContainer;
    const MenuList = slots?.menuList || DefaultMenuList;
    const SearchContainer = slots?.searchContainer || DefaultSearchContainer;

    return (
      <BaseSelectMenuRoot
        ref={ref}
        role="button"
        tabIndex={-1}
        className={classes.container}
        onKeyDown={handleCloseMenu}
        data-qa-id="base-select-menu-container"
        {...rest}>
        {isSearchable && (
          <SearchContainer className={classes.searchContainer}>
            <Search SearchInputProps={SearchInputProps} />
          </SearchContainer>
        )}
        {!isLoading ? (
          <MenuContainer
            role="button"
            tabIndex={-1}
            className={classes.menu}
            data-qa-id="base-select-menu"
            ref={element => {
              if (element && isAutoFocusMenu) {
                menuElRef.current = element;
              }
            }}
            onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
            }}>
            {options.length ? (
              <Menu
                MenuProps={{ ...MenuProps, isMultiSelectionMode }}
                options={options}>
                <MenuList
                  role="button"
                  data-qa-id="base-select-menu-list"
                  className={classes.menuList}
                  ref={menuListElRef}
                  tabIndex={0}
                  hasMenuHeader={hasMenuHeader}
                  onKeyPress={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    const haveFocusableOptions = focusableOptions.length;
                    if (haveFocusableOptions) {
                      const focusedItem = focusableOptions[0];
                      setFocusedValue(focusedItem.value);
                      if (onFocusedItemChange) {
                        onFocusedItemChange(focusedItem);
                      }
                    }
                  }}
                  onBlur={() => {
                    setFocusedValue(null);
                  }}>
                  {optionsToRender.map((item, idx) => {
                    navigationIterator++;
                    const groupName = item.groupName;
                    const isGroupCollapsed = !!collapsedGroups[groupName];

                    if (item.isGroup) {
                      const selectedOptions = MenuProps?.selectedOptions || [];

                      const isSelectedItem = item => {
                        return selectedOptions.some(
                          option => option.value === item.value
                        );
                      };
                      const isAllGroupItemsSelected = item.items.every(
                        isSelectedItem
                      );
                      const isSomeGroupItemsSelected = item.items.some(
                        isSelectedItem
                      );

                      const isGroupIndeterminate =
                        !isAllGroupItemsSelected && isSomeGroupItemsSelected;

                      const isAllGroupItemsDisabled = item.items.every(
                        item => item.isDisabled
                      );

                      return (
                        <div key={idx}>
                          <div>
                            <CategoryOption
                              component={Option}
                              items={item.items}
                              isCheckbox={isMultiSelectionMode}
                              isSelected={isAllGroupItemsSelected}
                              isIndeterminate={isGroupIndeterminate}
                              data={{ value: groupName, label: groupName }}
                              isFocused={item.value === focusedValue}
                              isGroupCollapsed={isGroupCollapsed}
                              showGroupCounter={incomingShowGroupCounter}
                              innerRef={createRefSetter({
                                item,
                                index: navigationIterator,
                                isFocusable: true
                              })}
                              onClick={() => handleGroupClick(groupName)}
                              CheckboxProps={{
                                isDisabled: isAllGroupItemsDisabled,
                                onClick: e => {
                                  onClick(item, e);
                                }
                              }}
                              data-qa-id={`option-${navigationIterator}`}
                            />
                            <Collapse in={!isGroupCollapsed}>
                              {item.items.map(nestedItem => {
                                navigationIterator++;
                                const isSelected = values.some(
                                  option => option.value === nestedItem.value
                                );

                                const isPreselected = preSelectedValues.some(
                                  option => option.value === nestedItem.value
                                );

                                const isItemDisabled = isDisable(nestedItem);
                                const dataKey = `${nestedItem.value}_${isGroupCollapsed}_${navigationIterator}`;
                                return (
                                  // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
                                  <Option
                                    // we are forcing the component to re:mount with a key so it will call the innerRef function again
                                    // when the option is toggled between collapsed and visible mode
                                    key={dataKey}
                                    // We are passing data-internal-key so we can use it in our tests, React is hiding the key prop from us :(
                                    // data-internal-key is not a public API
                                    data-internal-key={dataKey}
                                    data={nestedItem}
                                    isGroup={false}
                                    isNested
                                    isCheckbox={isMultiSelectionMode}
                                    groupName={nestedItem.groupName}
                                    isFocused={
                                      nestedItem.value === focusedValue
                                    }
                                    isSelected={isSelected}
                                    isDisabled={isItemDisabled}
                                    innerRef={createRefSetter({
                                      item: nestedItem,
                                      index: navigationIterator,
                                      isFocusable:
                                        isPreselected ||
                                        (!isItemDisabled && !isGroupCollapsed)
                                    })}
                                    lastSelected={lastSelected}
                                    preSelectedValues={preSelectedValues}
                                    data-qa-id={`option-${navigationIterator}`}
                                    onClick={e => {
                                      if (!isItemDisabled) {
                                        onClick(nestedItem, e);
                                      }
                                    }}
                                    onMouseOver={
                                      onFocusedItemChange
                                        ? () => onFocusedItemChange(nestedItem)
                                        : undefined
                                    }
                                  />
                                );
                              })}
                            </Collapse>
                          </div>
                        </div>
                      );
                    } else {
                      const isSelected = values.some(
                        option => option.value === item.value
                      );
                      const isPreselected = preSelectedValues.some(
                        option => option.value === item.value
                      );
                      const isItemDisabled = isDisable(item);
                      const dataKey = `${
                        item.value
                      }_${!!isGroupCollapsed}_${idx}`;

                      return (
                        // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
                        <Option
                          // we are forcing the component to re:mount with a key so it will call the innerRef function again
                          // when the option is toggled between collapsed and visible mode
                          key={dataKey}
                          // We are passing data-internal-key so we can use it in our tests, React is hiding the key prop from us :(
                          // data-internal-key is not a public API
                          data-internal-key={dataKey}
                          data={item}
                          isGroup={false}
                          isCheckbox={isMultiSelectionMode}
                          groupName={item.groupName}
                          isFocused={item.value === focusedValue}
                          isSelected={isSelected}
                          isDisabled={isItemDisabled}
                          innerRef={createRefSetter({
                            item,
                            index: navigationIterator,
                            isFocusable:
                              isPreselected ||
                              (!isItemDisabled && !isGroupCollapsed)
                          })}
                          lastSelected={lastSelected}
                          preSelectedValues={preSelectedValues}
                          data-qa-id={`option-${idx}`}
                          onClick={e => {
                            if (isItemDisabled) {
                              return;
                            }

                            onClick(item, e);
                          }}
                          onMouseOver={
                            onFocusedItemChange
                              ? () => onFocusedItemChange(item)
                              : undefined
                          }
                        />
                      );
                    }
                  })}
                </MenuList>
              </Menu>
            ) : (
              <NoOptionsMessage
                className={classes.noOptionsMessage}
                MenuProps={MenuProps}
              />
            )}
          </MenuContainer>
        ) : (
          <LoadingMessage className={classes.loadingMessage} />
        )}
      </BaseSelectMenuRoot>
    );
  }
);

BaseSelectMenu.propTypes = {
  menuIsOpen: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  value: PropTypes.oneOfType([
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.any
      })
    )
  ]),
  lastSelected: PropTypes.object,
  preSelectedValues: PropTypes.oneOfType([
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
      /** Support groups */
      groupName: PropTypes.string
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.any,
        /** Support groups */
        groupName: PropTypes.string
      })
    )
  ]),
  secondaryText: PropTypes.string,
  renderIcon: PropTypes.func,
  isSearchable: PropTypes.bool,
  onKeyDown: PropTypes.func,
  onClick: PropTypes.func,
  isOptionDisabled: PropTypes.func,
  SearchInputProps: PropTypes.object,
  MenuProps: PropTypes.object,
  isLoading: PropTypes.bool,
  components: PropTypes.object,
  reverseOptionsOrder: PropTypes.bool,
  showGroupCounter: PropTypes.bool,
  slots: PropTypes.object
};

BaseSelectMenu.displayName = 'BaseSelectMenu';

export default BaseSelectMenu;
